import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, Check, X, Award, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FILTERS = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["", "All"]];

export default function TestimonialsModerationPanel() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const { data: items = [] } = useQuery({ queryKey: ["admin-testimonials", status], queryFn: () => api.get(`/admin/testimonials/all${status ? `?status=${status}` : ""}`).then((r) => r.data) });
  const act = async (id, action) => {
    try {
      if (action === "delete") { if (!window.confirm("Delete this testimonial?")) return; await api.delete(`/testimonials/${id}`); }
      else await api.post(`/admin/testimonials/${id}/moderate`, { action });
      toast.success("Updated — your changes are live");
      qc.invalidateQueries({ queryKey: ["admin-testimonials"] });
      qc.invalidateQueries({ queryKey: ["testimonials"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="admin-testimonials-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Testimonial moderation</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">Approve, feature, or reject member-submitted testimonials.</p>
      <div className="flex gap-2 mb-6">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} data-testid={`testimonial-filter-${v || "all"}`}
            className={`px-4 py-1.5 rounded-full text-sm border ${status === v ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>{l}</button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="eyw-card p-5" data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex gap-0.5">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={14} className="fill-[#D98E73] text-[#D98E73]" />)}</div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === "approved" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : t.status === "rejected" ? "bg-red-100 text-red-600" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>{t.status}{t.featured ? " · featured" : ""}</span>
            </div>
            <p className="text-sm text-[#4A5D6B] mt-2 italic">"{t.quote}"</p>
            <p className="text-xs text-[#8FA89B] mt-2">— {t.name} ({t.role}){t.subject_name ? ` · ${t.subject_name}` : ""}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {t.status !== "approved" && <button onClick={() => act(t.id, "approve")} data-testid={`testimonial-approve-${t.id}`} className="eyw-btn !py-1.5 !px-3 text-xs"><Check size={13} /> Approve</button>}
              {!t.featured && <button onClick={() => act(t.id, "feature")} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Award size={13} /> Feature</button>}
              {t.featured && <button onClick={() => act(t.id, "unfeature")} className="eyw-btn-ghost !py-1.5 !px-3 text-xs">Unfeature</button>}
              {t.status !== "rejected" && <button onClick={() => act(t.id, "reject")} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><X size={13} /> Reject</button>}
              <button onClick={() => act(t.id, "delete")} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#4A5D6B] text-sm">No testimonials in this view.</p>}
      </div>
    </div>
  );
}
